import { MINUTE_IN_MILLISECONDS } from "shared/constants";

export default function Poller({
  callback = () => {},
  interval = MINUTE_IN_MILLISECONDS,
  pauseBackgroundTabs = false,
}) {
  let timeout = null;
  let lastRunAt = null;
  let initialized = false;

  // Bumped by pause()/stop() so a run whose callback is still pending knows it
  // was cancelled and must not rearm the timer once it settles.
  let generation = 0;

  async function executeCallback() {
    await callback();
    lastRunAt = Date.now();
  }

  const changePollerStateOnVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      if (lastRunAt + interval < Date.now()) {
        this.run();
      } else {
        this.start();
      }
    } else {
      this.pause();
    }
  };

  this.init = ({ immediate } = {}) => {
    if (initialized) return;

    this.start({ immediate });

    // Pause the polling when app is in background
    document.addEventListener("pause", this.pause, false);
    document.addEventListener("resume", this.start, false);

    // Desktop - Pause polling when tab is not active
    if (pauseBackgroundTabs) {
      document.addEventListener(
        "visibilitychange",
        changePollerStateOnVisibilityChange,
        false
      );
    }

    initialized = true;
  };

  this.start = async ({ immediate } = {}) => {
    if (timeout) this.pause();

    const runGeneration = generation;

    if (immediate) await executeCallback();

    if (runGeneration !== generation) return;

    timeout = setTimeout(() => this.run(), interval);
  };

  this.pause = () => {
    generation += 1;

    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  this.stop = () => {
    document.removeEventListener("pause", this.pause, false);
    document.removeEventListener("resume", this.start, false);

    // Desktop - Pause polling when tab is not active
    document.removeEventListener(
      "visibilitychange",
      changePollerStateOnVisibilityChange,
      false
    );

    this.pause();
    initialized = false;
  };

  this.run = async () => {
    const runGeneration = generation;

    await executeCallback();

    // stop()/pause() landed while the callback was in flight; rearming here
    // would resurrect polling after logout or in a backgrounded app.
    if (runGeneration !== generation) return;

    this.start();
  };
}
